import { Store } from '@ngrx/store';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { RecommendationsByPlacementParamsGroup } from '../../domain/models/recommendations-by-placement.model';
import { ProductUid } from '../../domain/models/related-products.models';
import { RelatedProductsRepositoryInterface } from '../../domain/related-products-repository.interface';
import { RelatedProductTypeConfig } from '../../infrastructure/state/related-products.state';

import { getCompleteLookYodaProductsUseCase } from './complete-look-yoda.uc';
import { getRelatedProductsFromFlorenUseCase } from './floren.use-case';
import { getRecentlyViewedProducts } from './recently-viewed.use-case';
import { getRecommendationsByPlacement } from './recommendations-by-placement.uc';
import { getWishlistProductsUseCase } from './wishlist.use-case';

import { CartItem } from '@api/cart/cart.models';
import { ProductPublicModels } from '@features/products/product';
import { WishlistQueriesFacade } from '@features/wishlist/infrastructure/facades/queries/wishlist.queries.facades';
import { ProductViewPlace } from '@shared/product/models';
import { ProductViewedService } from '@shared/product/services/product-viewed.service';
import { StaticResourceService } from '@shared/services/static-resources/static-resource.service';

// #region PRIVATE
const mapProductsWithNextIndex = (
    currentIndex: number,
    itemsConfig: RelatedProductTypeConfig[],
    products: ProductUid[],
): { productsMapped: ProductUid[]; nextIndex: number } => {
    const productsMapped: ProductUid[] = products.map(
        product => ({ ...product, relatedType: itemsConfig[currentIndex].name })
    );

    const nextIndex: number = currentIndex + 1;

    return { productsMapped, nextIndex };
};

// TO REFACTOR: Rename to be more specific
const getObservableRelatedType = (
    relatedProductsRepository: RelatedProductsRepositoryInterface,
    staticResourceService: StaticResourceService,
    productViewedService: ProductViewedService,
    store: Store,
    wishlistQueriesFacade: WishlistQueriesFacade,
    type: string,
    section: number,
    baseProducts: Array<ProductPublicModels.PublicProduct | CartItem>,
    sessionId: string,
    placeName: ProductViewPlace,
    campaign: string,
    limit: number,
): Observable<ProductUid[]> => {
    switch (type) {
        case 'similares_yoda':
        case 'cart_yoda':
        case 'frecuently_bought_together':
        case 'other_you_may_like':
        case 'recommended_for_you':
        case 'top_sales': {
            const paramsGroup: RecommendationsByPlacementParamsGroup = {
                placeName,
                type,
                sessionId,
                section,
                campaign,
                limit,
                baseProducts: baseProducts || [],
            };
            return getRecommendationsByPlacement(
                relatedProductsRepository,
                store,
                paramsGroup,
            );
        }
        case 'completeLookYoda': // New algorthm
        case 'completatulook_yoda': // Legacy algorthm
            return getCompleteLookYodaProductsUseCase(
                relatedProductsRepository,
                store,
                baseProducts[0] as ProductPublicModels.PublicProduct,
                section,
            );
        case 'floren':
            return getRelatedProductsFromFlorenUseCase(
                staticResourceService,
                baseProducts[0] as ProductPublicModels.PublicProduct
            );
        case 'recentlyViewed':
            return getRecentlyViewedProducts(store, productViewedService);
        case 'wishlist':
            return getWishlistProductsUseCase(store, wishlistQueriesFacade);
        default:
            return of([]);
    }
};
// #endregion PRIVATE

// #region PUBLIC
export const getRelatedProductIdsUseCase = (
    // #region DEPENDENCIES so many dependencies in this function that it is difficult to understand what it does at a glance
    staticResourceService: StaticResourceService,
    store: Store,
    productViewedService: ProductViewedService,
    relatedProductsRepository: RelatedProductsRepositoryInterface,
    wishlistQueriesFacade: WishlistQueriesFacade,
    // #endregion DEPENDENCIES
    itemsConfig: RelatedProductTypeConfig[],
    section: number,
    currentIndex: number,
    baseProducts: Array<ProductPublicModels.PublicProduct | CartItem>,
    sessionId: string,
    placeName: ProductViewPlace,
): Observable<ProductUid[]> => {
    const handleRecursion = (products: ProductUid[] = []) => {
        const { productsMapped, nextIndex } = mapProductsWithNextIndex(
            currentIndex,
            itemsConfig,
            products,
        );

        return getRelatedProductIdsUseCase(
            staticResourceService,
            store,
            productViewedService,
            relatedProductsRepository,
            wishlistQueriesFacade,
            itemsConfig,
            section,
            nextIndex,
            baseProducts,
            sessionId,
            placeName,
        ).pipe(
            map((recursiveProducts) => productsMapped.concat(recursiveProducts))
        );
    };

    if (currentIndex >= itemsConfig.length) {
        return of([]);
    }

    const currentItemConfig: RelatedProductTypeConfig = itemsConfig[currentIndex];

    return getObservableRelatedType(
        relatedProductsRepository,
        staticResourceService,
        productViewedService,
        store,
        wishlistQueriesFacade,
        currentItemConfig.name,
        section,
        baseProducts,
        sessionId,
        placeName,
        currentItemConfig.campaign,
        currentItemConfig.maxItems,
    ).pipe(
        switchMap(products => handleRecursion(products)),
        catchError((error: unknown) => {
            console.error(error);
            return handleRecursion();
        }),
    );
};
// #endregion PUBLIC

// #region __TESTS__
export const __tests__ = {
    getObservableRelatedType,
    mapProductsWithNextIndex,
};
